import { Drawer, Pagination, Table } from '@/components/commons';
import Form from '@/components/commons/Form';
import { ENDPOINT } from '@/services/endpoint';
import { GameIntroductionService } from '@/services/gameIntroduction';
import { ImageService } from '@/services/image';
import { useGameIntroStore } from '@/store';
import { IResponse, IResponseError } from '@/types/common';
import {
  EGameIntroductionType,
  GameIntroductionFormValues,
  IGameIntroductionBody,
} from '@/types/request';
import { IUploadImageRequest } from '@/types/request/upload';
import {
  IGetDetailGameIntroductionResponse,
  IGetListGameIntroductionResponse,
} from '@/types/response';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
  Status,
  statusJP,
} from '@/utils';
import { SUCCESS_MESSAGE } from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, TableColumnsType } from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { GameIntroductionForm } from '../Drawers';
import { INITIAL_VALUES } from '../Drawers/GameIntroductionForm/constants';
import { formSchemaFn } from '../Drawers/GameIntroductionForm/formSchemaFn';
import { GameIntroFilter } from './GameIntroFilter';
import moment from 'moment';

interface IParams {
  limit: number;
  page: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export const GameIntroductionList: React.FC<{ title?: string }> = ({
  title,
}) => {
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);

  const { openDetail, filter, id, onOpenDetail, onCloseDetail, setParams } =
    useGameIntroStore();

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.GAME_INTRODUCTION.GET_LIST, filter],
    () => GameIntroductionService.getList(convertRequest(filter)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!id)),
    reValidateMode: 'onChange',
    defaultValues: INITIAL_VALUES,
  });

  const { handleSubmit, reset, setError } = methods;

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );
  const { data: gameIntro, isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.GAME_INTRODUCTION.GET_DETAIL.replace(':id', String(id))],
    () => GameIntroductionService.getDetail(id!),
    {
      enabled: !!id,
      onSuccess: (response: IResponse<IGetDetailGameIntroductionResponse>) => {
        const d = response?.data;
        reset({
          title: d?.title,
          appName: d?.appName,
          type: d?.type,
          icon: d?.iconImage?.url || null,
          cover: d?.coverImage?.url || null,
          iconAttachments: null,
          coverAttachments: null,
          publicAt: d?.publicAt ? moment(d.publicAt) : null,
          publicEndAt: d?.publicEndAt ? moment(d.publicEndAt) : null,
          status: d?.status ?? 1,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const uploadSingle = async (file?: File) => {
    if (!file) return null;
    const response = await uploadFile(file);
    return response?.data?.url || null;
  };

  const handleCreate = async (values: GameIntroductionFormValues) => {
    const { iconAttachments, coverAttachments, cover, icon, ...rest } = values;

    console.log('values', values);

    const iconFile: File | undefined =
      iconAttachments?.fileList?.[0]?.originFileObj;
    const coverFile: File | undefined =
      coverAttachments?.fileList?.[0]?.originFileObj;

    const [iconUrl, coverUrl] = await Promise.all([
      uploadSingle(iconFile),
      uploadSingle(coverFile),
    ]);

    const payload: IGameIntroductionBody = {
      ...rest,
      iconImageUrl: iconUrl ?? icon ?? null,
      coverImageUrl: coverUrl ?? cover ?? null,
      isActive: true,
    };

    await GameIntroductionService.create(payload);
  };

  const handleUpdate = async (
    values: GameIntroductionFormValues,
    id: number,
  ) => {
    const { iconAttachments, coverAttachments, cover, icon, ...rest } = values;

    console.log('values', values);

    const iconFile: File | undefined =
      iconAttachments?.fileList?.[0]?.originFileObj;
    const coverFile: File | undefined =
      coverAttachments?.fileList?.[0]?.originFileObj;

    const [iconUrl, coverUrl] = await Promise.all([
      uploadSingle(iconFile),
      uploadSingle(coverFile),
    ]);

    const payload: IGameIntroductionBody = {
      ...rest,
      iconImageUrl: iconUrl ?? icon ?? null,
      coverImageUrl: coverUrl ?? cover ?? null,
      isActive: true,
    };

    await GameIntroductionService.update(payload, Number(id));
  };

  const handleDelete = () => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteItem(id!),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  const { mutate: createItem, isLoading: isLoadingCreate } = useMutation(
    (body: GameIntroductionFormValues) => handleCreate(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: SUCCESS_MESSAGE.CREATE_GAME_INTRODUCTION,
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        onCloseDetail();
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutate: updateItem, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: GameIntroductionFormValues; id: number }) =>
      handleUpdate(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message: SUCCESS_MESSAGE.UPDATE_GAME_INTRODUCTION,
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        onCloseDetail();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { mutate: deleteItem, isLoading: isLoadingDelete } = useMutation(
    (id: number) => GameIntroductionService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message: SUCCESS_MESSAGE.DELETE_GAME_INTRODUCTION,
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        onCloseDetail();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const columns: TableColumnsType<IGetListGameIntroductionResponse> = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: '5%',
      align: 'center',
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      width: '20%',
    },
    {
      title: '遷移先 ',
      dataIndex: 'type',
      width: '20%',
      align: 'center',
      render: (type) =>
        type === EGameIntroductionType.NOUNOW ? 'のうKNOW' : 'Cogevo',
    },
    {
      title: '公開日時',
      dataIndex: 'publicAt',
      width: '20%',
      align: 'center',
      render: (publicAt) =>
        publicAt ? `${moment(publicAt).format('YYYY年MM月DD日 HH:mm')}` : '-',
    },
    {
      title: '公開終了日時',
      dataIndex: 'publicEndAt',
      width: '20%',
      align: 'center',
      render: (publicEndAt) =>
        publicEndAt
          ? `${moment(publicEndAt).format('YYYY年MM月DD日 HH:mm')}`
          : '-',
    },
    {
      title: '公開ステータス',
      width: '15%',
      dataIndex: 'status',
      align: 'center',
      render: (value, record) => {
        const isPublic = moment(record.publicAt).isSameOrBefore(moment());

        return value === Status.PUBLIC
          ? isPublic
            ? statusJP.PUBLIC
            : statusJP.SCHEDULED
          : statusJP.DRAFT;
      },
    },
  ];

  useEffect(() => {
    if (isNotFoundDetail) {
      onCloseDetail();
      refetch();
    }
  }, [isNotFoundDetail]);

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ page, limit: pageSize });
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <div className='flex justify-between'>
          <GameIntroFilter />
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                onOpenDetail();
                reset({
                  ...INITIAL_VALUES,
                });
              }}
            >
              ゲーム紹介を作成する
            </Button>
          </header>
        </div>

        <div className='flex justify-between flex-col'>
          <Table
            loading={isFetching || isLoadingUpload}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onRow={(record) => ({
              onClick: () => onOpenDetail(record.id),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: 'ゲーム紹介はまだありません',
            }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={filter.limit}
              current={filter.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={!id ? 'ゲーム紹介新規作成' : 'ゲーム紹介詳細'}
        onOk={handleSubmit((values) =>
          id
            ? updateItem({ body: values, id: Number(id) })
            : createItem(values),
        )}
        onCancel={onCloseDetail}
        onDelete={id ? handleDelete : undefined}
        maskClosable={false}
        open={openDetail}
        okText={!id ? '保存 ' : '更新 '}
        loading={
          isLoadingUpdate ||
          isLoadingDelete ||
          isFetchingDetail ||
          isLoadingCreate ||
          isLoadingUpload
        }
        isDeleted={!!id}
      >
        <Form
          {...methods}
          layout='vertical'
          onSubmit={(values) =>
            id
              ? updateItem({ body: values, id: Number(id) })
              : createItem(values)
          }
        >
          <GameIntroductionForm
            isLoading={isFetchingDetail || isLoadingUpdate || isLoadingCreate}
            gameIntro={gameIntro?.data}
          />
        </Form>
      </Drawer>
    </div>
  );
};
